import type { MilestoneDatesDto, SourcingEventPatchDto } from "../../../../../autogen/bff-api";
import type { MilestoneKey } from "./milestones";

export const daysBetween = (anchorIso: string, dateIso: string): number =>
  Math.round((new Date(dateIso).getTime() - new Date(anchorIso).getTime()) / 86_400_000);

/**
 * The tilbudsfrist the rest of the plan hangs off. Identical to the real deadline for single-stage;
 * for a two-stage competition before the § 23-2 invitation the server projects one from the
 * forespørselsfrist, so the downstream milestones still have something to anchor on.
 */
export const tenderAnchor = (dates: MilestoneDatesDto): string | null =>
  dates.plannedTenderDeadline ?? dates.bidSubmissionDeadline ?? null;

const DAY_MS = 86_400_000;

const osloDay = (iso: string): string =>
  new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Oslo", year: "numeric", month: "2-digit", day: "2-digit" }).format(
    new Date(iso),
  );

export const calendarDaysBetween = (fromIso: string, toIso: string): number =>
  Math.round((Date.parse(osloDay(toIso)) - Date.parse(osloDay(fromIso))) / DAY_MS);

export const questionDeadlineBounds = (
  bidIso: string | null | undefined,
  announcedAt: number,
): { min: Date; max: Date } | null => {
  if (!bidIso) return null;
  const bid = Date.parse(bidIso);
  const latestDaysBefore = Math.ceil((bid - announcedAt) / DAY_MS) - 1;
  if (latestDaysBefore < 1) return null;
  return { min: new Date(bid - latestDaysBefore * DAY_MS), max: new Date(bid - DAY_MS) };
};

export const milestoneOffsetPatch = (
  nodeKey: MilestoneKey,
  pickedIso: string,
  dates: MilestoneDatesDto,
  // Two-stage competitions plan against a projected tilbudsfrist; the real one only exists after
  // the § 23-2 invitation, so the bid node moves the tender period instead of an absolute date.
  twoStage = false,
  requestDeadline: string | null = null,
): SourcingEventPatchDto | null => {
  switch (nodeKey) {
    case "questionDeadline": {
      const anchor = tenderAnchor(dates);
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            questionDaysBeforeBid: calendarDaysBetween(pickedIso, anchor),
          },
        },
      };
    }
    case "bidDeadline": {
      // Two-stage: the real tilbudsfrist is born at the § 23-2 invitation, so the buyer is moving
      // the planned tender period out from the forespørselsfrist instead.
      if (twoStage) {
        if (!requestDeadline) return null;
        return {
          eventData: {
            milestonePlan: {
              tenderPeriodOffsetDays: daysBetween(requestDeadline, pickedIso),
            },
          },
        };
      }
      return {
        eventData: {
          bidSubmissionDeadline: pickedIso,
        },
      };
    }
    case "evaluationStart": {
      const anchor = tenderAnchor(dates);
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            evaluationOffsetDays: daysBetween(anchor, pickedIso),
          },
        },
      };
    }
    case "awardDecision": {
      if (!dates.evaluationTarget) return null;
      return {
        eventData: {
          milestonePlan: {
            awardOffsetDays: daysBetween(dates.evaluationTarget, pickedIso),
          },
        },
      };
    }
    case "contractSigning": {
      const anchor = dates.karensDate ?? dates.awardTarget;
      if (!anchor) return null;
      return {
        eventData: {
          milestonePlan: {
            contractSigningOffsetDays: daysBetween(anchor, pickedIso),
          },
        },
      };
    }
    default:
      return null;
  }
};
